import { useCallback, useEffect, useRef } from 'react';
import { Flex, RemoteImage, useScreenSize } from '@banhala/ui-kit';
import type { RelatedEventProps, RelatedEventItem } from './types';
import type { AnyObject } from '../../../../types';
import { useOpenDeeplink } from '../../../../hooks';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';

const SINGLE_HORIZONTAL_PADDING = 16;
const RELATED_HORIZONTAL_PADDING = 12;
const RELATED_GAP = 12;
const GRID_GAP = 6;
const SINGLE_ASPECT_RATIO = 343 / 200;

export const RelatedEvent = ({
  entity,
  body,
  logging,
  isVisible,
  isAwaitingDataChange,
}: RelatedEventProps) => {
  const { width: screenWidth } = useScreenSize();
  const { openDeeplink } = useOpenDeeplink();
  const logAnalyticsEvent = useLogAnalyticsEvent();

  // 노출 1회 발화 dedup. 아이템(deeplink)이 바뀌면(필터 변경 등) 재발화 허용.
  const displayedKeyRef = useRef<string | null>(null);

  // 부모가 50% 가시이고 데이터 교체 윈도우가 아닐 때 DISPLAY_EVENT 1회 발화.
  // 단일 정적 블록이라 inner viewable 콜백이 없어 부모 isVisible 로 판정한다.
  // (CLICK_EVENT와 동일한 컴포넌트+아이템 로깅 머지 규칙)
  useEffect(() => {
    if (!isVisible || isAwaitingDataChange) return;
    if (!entity || entity.length === 0) return;
    const first = entity[0];
    const key = first.item.deeplink ?? first.item.thumbnail;
    if (displayedKeyRef.current === key) return;
    displayedKeyRef.current = key;
    logAnalyticsEvent({
      name: 'DISPLAY_EVENT',
      analytics: {
        ...logging.analytics,
        ...first.logging.analytics,
      },
      ably_only_log: {
        ...(logging.ably_only_log as AnyObject),
        ...(first.logging.ably_only_log as AnyObject),
      },
    });
  }, [isVisible, isAwaitingDataChange, entity, logging, logAnalyticsEvent]);

  const handlePress = useCallback(
    (item: RelatedEventItem, itemLogging: AnyObject) => {
      if (!item.deeplink) return;
      // iOS/Android 네이티브와 동일하게 CLICK_EVENT 사용. 컴포넌트 + 아이템 로깅 머지.
      logAnalyticsEvent({
        name: 'CLICK_EVENT',
        analytics: {
          ...logging.analytics,
          ...itemLogging.analytics,
        },
        ably_only_log: {
          ...(logging.ably_only_log as AnyObject),
          ...(itemLogging.ably_only_log as AnyObject),
        },
      });
      openDeeplink(item.deeplink);
    },
    [openDeeplink, logging, logAnalyticsEvent],
  );

  if (!entity || entity.length === 0) return null;

  const firstEntity = entity[0];
  const item = firstEntity.item;
  const itemLogging = firstEntity.logging;
  const layoutType = body.item.type;

  // SINGLE 레이아웃: 전폭 배너 이미지
  if (layoutType === 'SINGLE') {
    const imageWidth = screenWidth - SINGLE_HORIZONTAL_PADDING * 2;
    const imageHeight = imageWidth / SINGLE_ASPECT_RATIO;

    return (
      <Flex padding={{ horizontal: SINGLE_HORIZONTAL_PADDING }}>
        <Flex
          borderRadius={6}
          overflow="hidden"
          onPress={() => handlePress(item, itemLogging)}
        >
          <RemoteImage
            uri={item.thumbnail}
            width={imageWidth}
            height={imageHeight}
          />
        </Flex>
      </Flex>
    );
  }

  // LEFT_RELATED / RIGHT_RELATED 레이아웃
  const contentWidth =
    screenWidth - RELATED_HORIZONTAL_PADDING * 2 - RELATED_GAP;
  const halfWidth = contentWidth / 2;
  // 배너 이미지는 정사각형 영역에 맞춤
  const bannerSize = halfWidth;
  // 2×2 그리드: 각 셀 크기
  const gridCellSize = (halfWidth - GRID_GAP) / 2;

  const thumbnails = item.goods_thumbnails.slice(0, 4);

  const bannerImage = (
    <Flex
      borderRadius={4}
      overflow="hidden"
      onPress={() => handlePress(item, itemLogging)}
    >
      <RemoteImage uri={item.thumbnail} width={halfWidth} height={bannerSize} />
    </Flex>
  );

  const gridView = (
    <Flex flex={1} onPress={() => handlePress(item, itemLogging)}>
      <Flex.Row flexWrap="wrap" width={halfWidth} height={bannerSize}>
        {thumbnails.map((thumbUrl, index) => (
          <Flex
            key={index}
            borderRadius={4}
            overflow="hidden"
            margin={{
              right: index % 2 === 0 ? GRID_GAP : 0,
              bottom: index < 2 ? GRID_GAP : 0,
            }}
          >
            <RemoteImage
              uri={thumbUrl}
              width={gridCellSize}
              height={gridCellSize}
            />
          </Flex>
        ))}
      </Flex.Row>
    </Flex>
  );

  return (
    <Flex padding={{ horizontal: RELATED_HORIZONTAL_PADDING }}>
      <Flex.Row gap={RELATED_GAP}>
        {layoutType === 'LEFT_RELATED' ? (
          <>
            {bannerImage}
            {gridView}
          </>
        ) : (
          <>
            {gridView}
            {bannerImage}
          </>
        )}
      </Flex.Row>
    </Flex>
  );
};
